import React, { useContext, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import { XMarkIcon } from '@heroicons/react/20/solid';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../contexts/auth';
import { estraiMessaggiErrore } from '../services/utils';
import { getNextSigners, getSignedDoc, isSentToProvider } from '../services/api_firme';
import Fetching from './fetching';

const norm = (v) => String(v || '').trim().toLowerCase();

// Il provider restituisce i prossimi firmatari come stringhe (email in minuscolo, o "Nome Cognome"): si confrontano con email e CF (documenti legacy)
const isWaiting = (signer, nextSigners) =>
  nextSigners.some((n) => norm(n) === norm(signer.cf) || norm(n) === norm(signer.email));

const PILL = 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium';

export default function FirmeSignersDialog({ doc, onClose }) {
  const { token } = useContext(AuthContext);
  const [signers, setSigners] = useState([]);
  const [nextSigners, setNextSigners] = useState(null); // null = non disponibile
  const [loading, setLoading] = useState(Boolean(doc));
  const [error, setError] = useState(null);

  const docId = doc?.id;
  const inProgress = !!doc &&
    !doc.is_signed &&
    isSentToProvider(doc) &&
    !['ERROR', 'REJECTED'].includes(doc.status);

  useEffect(() => {
    if (!docId) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setSigners([]);
    setNextSigners(null);

    (async () => {
      try {
        const [detail, next] = await Promise.all([
          getSignedDoc(token, docId, controller.signal),
          // L'elenco dei prossimi firmatari è accessorio: se non arriva si mostrano solo i firmatari
          inProgress
            ? getNextSigners(token, docId, controller.signal).catch((err) => {
                if (axios.isCancel(err)) throw err;
                return null;
              })
            : Promise.resolve(null),
        ]);
        setSigners(Array.isArray(detail.firmatari) ? detail.firmatari : []);
        setNextSigners(next);
      } catch (err) {
        if (axios.isCancel(err)) return;
        setError(estraiMessaggiErrore(err));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [token, docId, inProgress]);

  // Stringhe di FirmoSemplice che non corrispondono a nessun firmatario noto: si mostrano così come sono
  const unmatched = (nextSigners || []).filter(
    (n) => !signers.some((s) => norm(n) === norm(s.cf) || norm(n) === norm(s.email))
  );

  // "Ha firmato" solo a documento completato: l'elenco di FirmoSemplice può contenere soltanto
  // il prossimo firmatario, quindi chi non compare non ha necessariamente già firmato.
  // Con OpenAPI invece l'elenco contiene tutti i firmatari che non hanno ancora firmato.
  const listsAllPending = doc?.provider_name === 'openapi';
  const statusOf = (signer) => {
    if (doc?.is_signed) {return { label: 'Ha firmato', className: 'bg-green-100 text-green-800' }}
    if (doc?.status === 'ERROR') {return { label: 'Firma non riuscita', className: 'bg-red-100 text-red-800' }}
    if (doc?.status === 'REJECTED') {return { label: 'Firma rifiutata', className: 'bg-orange-100 text-orange-800' }}
    if (inProgress && nextSigners && isWaiting(signer, nextSigners)) {return { label: 'Deve firmare', className: 'bg-yellow-100 text-yellow-800' }}
    if (inProgress && nextSigners && listsAllPending) {return { label: 'Ha firmato', className: 'bg-green-100 text-green-800' }}
    return null;
  };

  const canCopyLink = (signer) =>
    inProgress && !!signer.signUrl && (!nextSigners || isWaiting(signer, nextSigners));

  const copyLink = async (url) => {
    const toast = (icon, titleText) =>
      Swal.fire({ toast: true, position: 'top-end', icon, titleText, timer: 3000, showConfirmButton: false });
    try {
      await navigator.clipboard.writeText(url);
      toast('success', 'Link copiato');
    } catch (err) {
      toast('error', 'Impossibile copiare il link');
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={Boolean(doc)} onClose={onClose}>
      {doc && (
        <>
          <DialogTitle sx={{ p: 1.5 }} className="flex justify-between items-center">
            <span className="font-semibold truncate">Firmatari — {doc.name}</span>
            <IconButton onClick={onClose} aria-label="Chiudi">
              <XMarkIcon className="h-5 w-5" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {loading && <Fetching />}
            {!loading && error && <p className="text-sm text-red-600">{error}</p>}
            {!loading && !error && (
              <>
                {['ERROR', 'REJECTED'].includes(doc.status) && (
                  <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-sm font-semibold text-red-800">
                      {doc.status === 'REJECTED'
                        ? 'La procedura di firma è stata rifiutata.'
                        : 'La procedura di firma non è riuscita.'}
                    </p>

                    {doc.provider_error_message && (
                      <p className="mt-1 text-sm text-red-700">
                        {doc.provider_error_message}
                      </p>
                    )}

                    {doc.provider_error_code != null && (
                      <p className="mt-1 text-xs text-red-600">
                        Codice errore: {doc.provider_error_code}
                      </p>
                    )}
                  </div>
                )}
                {!isSentToProvider(doc) && !doc.is_signed && (
                  <p className="mb-3 text-sm text-gray-600">
                    Il documento non è stato inviato al servizio di firma: nessun firmatario ha ricevuto l&apos;email.
                  </p>
                )}
                {signers.length === 0 ? (
                  <p className="text-sm text-gray-500">Nessun firmatario registrato per questo documento.</p>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {signers.map((s, i) => {
                      const st = statusOf(s);
                      return (
                        <li key={`${s.cf}-${i}`} className="flex items-center justify-between gap-3 py-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {s.name} {s.surname}
                            </p>
                            <p className="text-sm text-gray-500 truncate">{s.email}</p>
                            {s.descrizione && <p className="text-xs text-gray-500 truncate">{s.descrizione}</p>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {canCopyLink(s) && (
                              <button
                                type="button"
                                onClick={() => copyLink(s.signUrl)}
                                className="rounded-md bg-white px-2 py-1 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                              >
                                Copia link
                              </button>
                            )}
                            {st && <span className={`${PILL} ${st.className}`}>{st.label}</span>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {inProgress && nextSigners === null && (
                  <p className="mt-3 text-xs text-gray-500">
                    Lo stato dei singoli firmatari non è al momento disponibile dal servizio di firma.
                  </p>
                )}
                {unmatched.length > 0 && (
                  <p className="mt-3 text-xs text-gray-500">
                    In attesa di firma secondo il servizio di firma: {unmatched.join(', ')}
                  </p>
                )}
              </>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}
